import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";

import {
  SupplierFormValues,
  SupplierPayload,
  SupplierResource,
} from "../types";
import { useCreateSupplierMutation } from "../hooks/useCreateSupplierMutation";
import { useUpdateSupplierMutation } from "../hooks/useUpdateSupplierMutation";
import { SuppliersForm } from "./SuppliersForm";

const optionalString = () =>
  z
    .string()
    .optional()
    .transform((value) => value?.trim() ?? "");

const SUPPLIER_FORM_SCHEMA = z.object({
  id: z.string().optional(),
  name: z
    .string({ required_error: "O nome do fornecedor é obrigatório" })
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, "O nome do fornecedor é obrigatório"),
  taxNumber: optionalString(),
  description: optionalString(),
  phone: optionalString(),
  street: optionalString(),
  city: optionalString(),
  location: optionalString(),
  zipCode: optionalString(),
  country: optionalString(),
  email: optionalString().superRefine((value, ctx) => {
    if (!value) {
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Verifique o formato do email inserido",
      });
    }
  }),
});

type SupplierFormSchema = z.infer<typeof SUPPLIER_FORM_SCHEMA>;

type SuppliersFormControllerProps = {
  open: boolean;
  mode: "create" | "edit";
  defaultValues: SupplierFormValues;
  onClose: () => void;
  onCancel: () => void;
  onCreated: (supplier: SupplierResource) => void;
  onUpdated: (supplier: SupplierResource) => void;
};

const DEFAULT_VALUES: SupplierFormValues = {
  id: undefined,
  name: "",
  taxNumber: "",
  description: "",
  phone: "",
  email: "",
  street: "",
  city: "",
  location: "",
  zipCode: "",
  country: "",
};

export const SuppliersFormController: React.FC<SuppliersFormControllerProps> = ({
  open,
  mode,
  defaultValues,
  onClose,
  onCancel,
  onCreated,
  onUpdated,
}) => {
  const { mutateAsync: createSupplier, isPending: isCreating } =
    useCreateSupplierMutation();
  const { mutateAsync: updateSupplier, isPending: isUpdating } =
    useUpdateSupplierMutation();

  const form = useForm<SupplierFormSchema>({
    resolver: zodResolver(SUPPLIER_FORM_SCHEMA),
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
  });

  React.useEffect(() => {
    form.reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      const payload: SupplierFormValues = {
        ...DEFAULT_VALUES,
        ...values,
      };

      const basePayload: SupplierPayload = {
        name: payload.name,
        taxNumber: payload.taxNumber,
        description: payload.description,
        phone: payload.phone,
        email: payload.email,
        street: payload.street,
        city: payload.city,
        location: payload.location,
        zipCode: payload.zipCode,
        country: payload.country,
      };

      if (mode === "edit" && payload.id) {
        const supplier = await updateSupplier({ ...basePayload, id: payload.id });
        toast.success("Fornecedor atualizado.");
        onUpdated(supplier);
      } else {
        const supplier = await createSupplier(basePayload);
        toast.success("Fornecedor criado.");
        onCreated(supplier);
      }

      form.reset({ ...DEFAULT_VALUES });
      onClose();
    } catch (error) {
      toast.error("Não foi possível guardar o fornecedor. Tente novamente.");
    }
  });

  const handleCancel = () => {
    onCancel();
    onClose();
    form.reset({ ...DEFAULT_VALUES, ...defaultValues });
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <SuppliersForm
      open={open}
      mode={mode}
      form={form}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onClose={onClose}
    />
  );
};
